import { useEffect, useRef } from 'react';
import { F, haptic, Z } from './theme.jsx';
import { useToast } from './components/boothly/toast-provider.jsx';
import { subscribeSwipeProgress } from './hooks/gesture/swipe-progress.js';

// iOS sliding-capsule easing (matches --ease-out-ios convention used across the app)
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Nav geometry — single source of truth for the nav's height + bottom gap.
// The wrapper padding and the pill minHeight both derive from these, and the
// floating ActionButton imports them so its gap above the nav stays correct.
export const NAV_BOTTOM_GAP = 6;       // base wrapper padding-bottom above the safe-area inset
export const NAV_FLOATING_HEIGHT = 54; // glass-pill height (locked via minHeight below)
// Actual bottom offset of the nav pill — sits 30% closer to the screen edge than the base gap.
// Exported so the floating ActionButton stays in sync and keeps its 16px gap above the nav.
export const NAV_BOTTOM_OFFSET = `calc((env(safe-area-inset-bottom, 0px) + ${NAV_BOTTOM_GAP}px) * 0.7)`;

/**
 * FloatingBottomNav — purely presentational floating glass bottom-nav.
 *
 * One shared look across all areas (global, online, troedel): a floating glass
 * pill with a neutral sliding capsule (var(--label-primary)) for the active tab.
 * The `accent` prop only drives the per-area glow/identity — never the capsule.
 *
 * Hide-when-sheet-open logic and z-index live at the call sites; this component
 * is intentionally dumb.
 *
 * Props:
 *   items         [{ id, label, icon, badge?, disabled?, disabledHint? }]
 *   activeId      string
 *   onSelect      (id) => void
 *   accent        CSS-variable string, e.g. 'var(--accent-online)' | 'var(--label-primary)'
 *   dark          boolean (only used for the badge ring colour)
 *   swipeProgress -1..1 (optional, subtly intensifies the glass blur)
 */
export function FloatingBottomNav({
  items,
  activeId,
  onSelect,
  accent = 'var(--label-primary)',
  dark,
  swipeProgress = 0,
  showLabels = true,
}) {
  const toast = useToast();
  const pillRef = useRef(null);
  const capsuleRef = useRef(null);
  const safeItems = Array.isArray(items) ? items : [];
  const count = safeItems.length;

  // Live-Swipe-Fortschritt (Bus aus SwipeableTabs): Kapsel folgt dem Finger, Glas-Blur
  // verstärkt sich leicht — direkt ins DOM, ohne React-Render pro Frame.
  useEffect(() => subscribeSwipeProgress((p, meta) => {
    const pill = pillRef.current;
    const cap = capsuleRef.current;
    if (!pill || !cap || !count) return;
    // React-Inline-Werte merken und nach dem Swipe exakt wiederherstellen
    if (cap.dataset.baseTransition === undefined) cap.dataset.baseTransition = cap.style.transition;
    const step = (pill.clientWidth - 12) / count;
    cap.style.transform = p ? `translate3d(${p * step}px, 0, 0)` : '';
    if (meta?.commit) {
      // neue `left`-Position kommt mit dem nächsten Render — ohne Transition, dann wieder an
      cap.style.transition = 'none';
      requestAnimationFrame(() => requestAnimationFrame(() => { cap.style.transition = cap.dataset.baseTransition; }));
    } else {
      cap.style.transition = p ? 'none' : cap.dataset.baseTransition;
    }
    const filter = `blur(${30 + Math.abs(p) * 6}px) saturate(180%)`;
    pill.style.backdropFilter = filter;
    pill.style.webkitBackdropFilter = filter;
  }), [count]);

  if (safeItems.length === 0) return null;

  const n = safeItems.length;
  const idx = Math.max(0, safeItems.findIndex((it) => it.id === activeId));

  const normalizedProgress = Math.max(-1, Math.min(1, Number.isFinite(swipeProgress) ? swipeProgress : 0));
  const blurPx = 30 + Math.abs(normalizedProgress) * 6; // 30px → ~36px while swiping

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: Z.bottomNav,
        padding: `0 16px ${NAV_BOTTOM_OFFSET} 16px`,
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {/* Scrim — content fades/darkens strongly behind the nav as it scrolls under */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 140,
          pointerEvents: 'none',
          background:
            'linear-gradient(to top, var(--bg-L0) 18%, color-mix(in srgb, var(--bg-L0) 55%, transparent) 55%, transparent 100%)',
        }}
      />

      {/* Floating glass pill */}
      <div
        ref={pillRef}
        data-testid="bottom-nav"
        style={{
          position: 'relative',
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: 360,
          minHeight: NAV_FLOATING_HEIGHT,
          display: 'flex',
          padding: 5, // → Tab-Ziele 44pt hoch (HIG)
          borderRadius: 30,
          background: 'var(--nav-fill)',
          border: '1px solid var(--nav-border)',
          backdropFilter: `blur(${blurPx}px) saturate(180%)`,
          WebkitBackdropFilter: `blur(${blurPx}px) saturate(180%)`,
          boxShadow: `var(--nav-shadow), inset 0 1px 0 var(--nav-shine), 0 6px 26px color-mix(in srgb, ${accent} 22%, transparent)`,
        }}
      >
        {/* Sliding active capsule — raised; light = accent-tinted, dark = matte grey (via .fbn-active-capsule).
            Positioned via `left` so % resolves against the pill. */}
        <span
          ref={capsuleRef}
          aria-hidden="true"
          className="fbn-active-capsule"
          style={{
            position: 'absolute',
            top: 5,
            bottom: 5,
            left: `calc(10px + ${idx} * (100% - 12px) / ${n})`,
            width: `calc((100% - 12px) / ${n} - 8px)`,
            borderRadius: 22,
            zIndex: 0,
            '--cap-accent': accent,
            transition: `left 0.42s ${EASE}, width 0.42s ${EASE}`,
          }}
        />

        {safeItems.map((item) => {
          const active = item.id === activeId;
          const disabled = !!item.disabled;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`bottom-nav-tab-${item.id}`}
              data-active={active ? 'true' : 'false'}
              onClick={() => {
                if (disabled) {
                  haptic(8);
                  toast.info(item.disabledHint || 'Aktion gerade nicht verfügbar');
                  return;
                }
                haptic(6);
                onSelect?.(item.id);
              }}
              style={{
                flex: 1,
                position: 'relative',
                zIndex: 1,
                background: 'none',
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: 44,
                padding: '2px 4px',
                fontFamily: F,
                color: active ? accent : 'var(--label-secondary)',
                opacity: disabled ? 0.38 : 1,
                transition: 'color 0.3s ease, opacity 0.18s ease',
              }}
            >
              <span
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  transform: active ? 'translateY(-1px) scale(1.04)' : 'none',
                  transition: `transform 0.3s ${EASE}`,
                }}
              >
                {item.icon}
                {item.badge ? (
                  <span
                    style={{
                      position: 'absolute',
                      top: -6,
                      left: '100%',
                      marginLeft: -4,
                      minWidth: item.badge.prominent ? 16 : 14,
                      height: item.badge.prominent ? 16 : 14,
                      padding: '0 4px',
                      borderRadius: 8,
                      background: item.badge.color,
                      color: '#fff',
                      fontSize: item.badge.prominent ? 10 : 9,
                      fontWeight: 700,
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontVariantNumeric: 'tabular-nums',
                      boxShadow: dark
                        ? '0 0 0 1.5px rgba(28,28,30,0.72)'
                        : '0 0 0 1.5px rgba(255,255,255,0.72)',
                    }}
                  >
                    {item.badge.count ?? item.badge.label ?? item.badge.value}
                  </span>
                ) : null}
              </span>
              {showLabels && (
                <span style={{ fontSize: 10, lineHeight: 1, fontWeight: 600, letterSpacing: '-0.01em' }}>
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
